// PresencasPublica.jsx — página própria /presencas/<token> (link colocado no portal do cliente)
// Entrada como no dashboard de fisioterapia: email autorizado → código de 6 dígitos por email → sessão de 7 dias neste browser.
// Os emails autorizados gerem-se no HUB, na ficha do cliente (Contas › Clientes › Dashboard de presenças).

import React, { useEffect, useState, useCallback } from "react";
import PresencasDashboard from "./PresencasDashboard";

const C = { roxo: "#691FB1", cinza: "#6B6880", linha: "#ECEAF5", fundo: "#FAF9FD", erro: "#BE1356", tinta: "#1E1A2B" };

export default function PresencasPublica({ supabase, token, logoSrc }) {
  const chave = `presencas_sessao_${token}`;
  const [sessao, setSessao] = useState(() => { try { return localStorage.getItem(chave) || ""; } catch { return ""; } });
  const [info, setInfo] = useState(null);        // null = a verificar · false = sem sessão · {cliente,titulo}
  const [fase, setFase] = useState("email");
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");
  const [aCarregar, setACarregar] = useState(false);

  const sair = useCallback((msg = "") => {
    try { localStorage.removeItem(chave); } catch { /* ignorar */ }
    setSessao(""); setInfo(false); setFase("email"); setCodigo(""); setErro(msg);
  }, [chave]);

  const obterDados = async ({ de, ate, local, setor, hora }) => {
    const { data, error } = await supabase.rpc("presencas_dashboard_dados", {
      p_token: token, p_sessao: sessao, p_de: de, p_ate: ate, p_local: local, p_setor: setor || null, p_hora: hora || null });
    if (error) { sair(/expirada|inv|retirado/i.test(error.message) ? "A sessão terminou. Peça um código novo." : error.message); return { data: null, error }; }
    return { data, error: null };
  };

  const abrir = useCallback(async (ses) => {
    const { data, error } = await supabase.rpc("presencas_dashboard_dados", { p_token: token, p_sessao: ses });
    if (error || !data) { sair(ses ? "A sessão terminou. Peça um código novo." : ""); return; }
    try { localStorage.setItem(chave, ses); } catch { /* ignorar */ }
    setSessao(ses); setInfo({ cliente: data.cliente, titulo: data.titulo });
  }, [supabase, token, chave, sair]);

  useEffect(() => { if (sessao) abrir(sessao); else setInfo(false); /* eslint-disable-next-line */ }, [token]);

  const pedirCodigo = async (e) => {
    e.preventDefault(); setACarregar(true); setErro(""); setAviso("");
    const { data, error } = await supabase.functions.invoke("presencas-codigo", { body: { token, email: email.trim() } });
    setACarregar(false);
    if (error || data?.erro) { setErro(data?.erro || "Não foi possível enviar o código. Tente de novo."); return; }
    setFase("codigo"); setAviso("Se o email estiver autorizado, o código chega dentro de momentos.");
  };
  const validarCodigo = async (e) => {
    e.preventDefault(); setACarregar(true); setErro("");
    const { data, error } = await supabase.rpc("presencas_codigo_validar", { p_token: token, p_email: email.trim(), p_codigo: codigo.trim() });
    setACarregar(false);
    if (error || !data || !data.length) { setErro("Código inválido ou expirado."); return; }
    abrir(data[0].sessao);
  };

  const campo = { width: "100%", padding: "10px 12px", border: `1px solid ${C.linha}`, borderRadius: 9, fontSize: 14, marginBottom: 12, boxSizing: "border-box" };
  const botao = { width: "100%", padding: "10px 12px", background: C.roxo, color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: "pointer" };

  return (
    <div style={{ minHeight: "100vh", background: C.fundo, fontFamily: "Helvetica, Arial, sans-serif" }}>
      <div style={{ background: "#fff", borderBottom: `1px solid ${C.linha}`, padding: "14px 20px", display: "flex", alignItems: "center" }}>
        {logoSrc ? <img src={logoSrc} alt="Workwell" style={{ height: 28 }} />
          : <span style={{ fontWeight: 800, fontSize: 20, color: C.roxo }}>workwell</span>}
        <span style={{ flex: 1 }} />
        {info && <button onClick={() => sair("")} style={{ background: "none", border: "none", color: C.cinza, fontSize: 12.5, cursor: "pointer" }}>Sair</button>}
      </div>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: 16 }}>
        {info === null && <div style={{ color: C.cinza, fontSize: 13, padding: 20 }}>A carregar…</div>}
        {info === false && (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 50 }}>
            <form onSubmit={fase === "email" ? pedirCodigo : validarCodigo}
              style={{ background: "#fff", border: `1px solid ${C.linha}`, borderRadius: 16, padding: 32, width: 380, maxWidth: "100%", boxSizing: "border-box" }}>
              <h1 style={{ fontSize: 17, margin: "0 0 6px", color: C.tinta }}>Dashboard de presenças</h1>
              {fase === "email" ? (<>
                <p style={{ fontSize: 13, color: C.cinza, margin: "0 0 18px" }}>Escreva o seu email. Enviamos-lhe um código para entrar.</p>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="o.seu.email@empresa.pt" autoFocus required style={campo} />
                {erro && <div style={{ color: C.erro, fontSize: 12.5, marginBottom: 12 }}>{erro}</div>}
                <button type="submit" disabled={aCarregar} style={botao}>{aCarregar ? "A enviar…" : "Enviar código"}</button>
              </>) : (<>
                <p style={{ fontSize: 13, color: C.cinza, margin: "0 0 18px" }}>{aviso || "Escreva o código que recebeu por email."}</p>
                <input value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric" autoFocus required
                  style={{ ...campo, fontSize: 22, letterSpacing: ".2em", textAlign: "center" }} />
                {erro && <div style={{ color: C.erro, fontSize: 12.5, marginBottom: 12 }}>{erro}</div>}
                <button type="submit" disabled={aCarregar || codigo.length < 6} style={{ ...botao, opacity: codigo.length < 6 ? 0.5 : 1 }}>{aCarregar ? "A validar…" : "Entrar"}</button>
                <button type="button" onClick={() => { setFase("email"); setCodigo(""); setErro(""); }} style={{ ...botao, background: "none", color: C.roxo, marginTop: 6 }}>Usar outro email</button>
              </>)}
              <p style={{ fontSize: 11.5, color: C.cinza, margin: "18px 0 0" }}>Depois de entrar, fica com acesso durante 7 dias neste browser. Dúvidas: apoio@workwell.pt</p>
            </form>
          </div>)}
        {info && <PresencasDashboard cliente={info.cliente} titulo={info.titulo} interno={false} obterDados={obterDados} />}
      </div>
    </div>
  );
}
